(function initializePiAccessoriesCarousel(root) {
  'use strict';

  const STORAGE_KEY = 'seacase_selected_phone_model';
  const LEGACY_STORAGE_KEY = 'seacase_selected_model';
  const MODEL_EVENT = 'seacase:phone-model-change';
  const LEGACY_MODEL_EVENT = 'seacase:model-changed';

  const normalize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s\-_\/()]+/g, '');

  const parseStoredModel = (storage, key) => {
    try {
      const parsed = JSON.parse(storage.getItem(key) || 'null');
      if (!parsed || !parsed.handle || !parsed.name) return null;
      return {
        handle: String(parsed.handle).trim(),
        name: String(parsed.name).trim(),
      };
    } catch (error) {
      return null;
    }
  };

  class PiAccessoriesCarousel extends HTMLElement {
    #controller = null;
    #resizeObserver = null;
    #scrollTimer = null;
    #cardStates = new WeakMap();
    #dragState = null;
    #blockNextClick = false;
    #adding = false;

    connectedCallback() {
      this.#controller?.abort();
      this.#resizeObserver?.disconnect();
      root.clearTimeout(this.#scrollTimer);

      this.#controller = new AbortController();
      const signal = this.#controller.signal;

      this.viewport = this.querySelector('[data-pia-viewport]');
      this.track = this.querySelector('[data-pia-track]');
      this.dots = this.querySelector('[data-pia-dots]');
      this.slides = Array.from(this.querySelectorAll('[data-pia-slide]'));
      this.visibleSlides = [];
      this.activeIndex = 0;

      if (!this.viewport || !this.track || !this.dots || this.slides.length === 0) {
        this.hidden = false;
        if (this.dots) this.dots.hidden = true;
        return;
      }

      this.slides.forEach((slide) => this.#initializeCard(slide));

      this.addEventListener('change', (event) => this.#handleModelChange(event), { signal });
      this.addEventListener('submit', (event) => this.#handleAddToCart(event), { signal });
      this.viewport.addEventListener('scroll', () => this.#handleScroll(), { signal, passive: true });
      this.viewport.addEventListener('pointerdown', (event) => this.#startDrag(event), { signal });
      this.viewport.addEventListener('pointermove', (event) => this.#moveDrag(event), { signal });
      this.viewport.addEventListener('pointerup', (event) => this.#endDrag(event), { signal });
      this.viewport.addEventListener('pointercancel', (event) => this.#endDrag(event), { signal });
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.viewport.addEventListener(
        'click',
        (event) => {
          if (!this.#blockNextClick) return;
          event.preventDefault();
          event.stopPropagation();
          this.#blockNextClick = false;
        },
        { capture: true, signal }
      );

      root.addEventListener(MODEL_EVENT, (event) => this.#applyGlobalModel(event.detail), { signal });
      document.addEventListener(LEGACY_MODEL_EVENT, (event) => this.#applyGlobalModel(event.detail), { signal });
      root.addEventListener(
        'storage',
        (event) => {
          if (event.key === STORAGE_KEY) this.#applyGlobalModel(this.#readSelectedModel());
        },
        { signal }
      );
      root.addEventListener(
        'pageshow',
        (event) => {
          if (event.persisted) this.#applyGlobalModel(this.#readSelectedModel());
        },
        { signal }
      );

      if ('ResizeObserver' in root) {
        this.#resizeObserver = new ResizeObserver(() => this.#goTo(this.activeIndex, 'auto'));
        this.#resizeObserver.observe(this.viewport);
      }

      const selectedModel = this.#readSelectedModel();
      if (selectedModel) {
        this.#applyGlobalModel(selectedModel);
      } else {
        this.#refreshSlides();
      }
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#resizeObserver?.disconnect();
      root.clearTimeout(this.#scrollTimer);
      this.slides?.forEach((slide) => {
        const card = slide.querySelector('[data-pia-card]');
        const state = card ? this.#cardStates.get(card) : null;
        root.clearTimeout(state?.resetTimer);
      });
    }

    #initializeCard(slide) {
      const card = slide.querySelector('[data-pia-card]');
      if (!card) {
        slide.hidden = true;
        return;
      }

      const variantsElement = card.querySelector('[data-pia-variants]');
      let variants = [];
      try {
        const parsed = JSON.parse(variantsElement?.textContent || '[]');
        variants = Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        variants = [];
      }

      const state = {
        card,
        slide,
        variants,
        hasModel: card.dataset.piaHasModel === 'true',
        modelPosition: Math.max(0, Number(card.dataset.piaModelPosition || 0) - 1),
        select: card.querySelector('[data-pia-model-select]'),
        modelLabel: card.querySelector('[data-pia-model-label]'),
        image: card.querySelector('.pi-accessory-card__image'),
        priceCurrent: card.querySelector('[data-pia-price-current]'),
        priceCompare: card.querySelector('[data-pia-price-compare]'),
        variantInput: card.querySelector('[data-pia-variant-id]'),
        addButton: card.querySelector('[data-pia-add]'),
        addText: card.querySelector('[data-pia-add-text]'),
        error: card.querySelector('[data-pia-error]'),
        currentVariant: null,
        resetTimer: null,
      };

      this.#cardStates.set(card, state);

      const initialVariant =
        variants.find((variant) => String(variant.id) === String(state.variantInput?.value || '')) ||
        variants.find((variant) => variant.available) ||
        variants[0] ||
        null;
      this.#renderVariant(state, initialVariant);
    }

    #readSelectedModel() {
      return (
        parseStoredModel(root.localStorage, STORAGE_KEY) ||
        parseStoredModel(root.sessionStorage, LEGACY_STORAGE_KEY)
      );
    }

    #findModelOption(state, model) {
      if (!state.hasModel || !state.select || !model) return null;
      const modelHandle = String(model.handle || '').trim();
      const modelName = normalize(model.name);

      return (
        Array.from(state.select.options).find(
          (option) => modelHandle && String(option.dataset.modelHandle || '') === modelHandle
        ) ||
        Array.from(state.select.options).find((option) => normalize(option.value) === modelName) ||
        null
      );
    }

    #findVariantForModel(state, modelValue) {
      const target = normalize(modelValue);
      if (!target) return null;

      const matches = state.variants.filter(
        (variant) =>
          Array.isArray(variant.options) &&
          normalize(variant.options[state.modelPosition]) === target
      );
      return matches.find((variant) => variant.available) || matches[0] || null;
    }

    #applyGlobalModel(model) {
      if (!model || !model.handle || !model.name) return;

      this.slides.forEach((slide) => {
        const card = slide.querySelector('[data-pia-card]');
        const state = card ? this.#cardStates.get(card) : null;
        if (!state) {
          slide.hidden = true;
          return;
        }

        if (!state.hasModel) {
          slide.hidden = false;
          return;
        }

        const option = this.#findModelOption(state, model);
        const variant = option ? this.#findVariantForModel(state, option.value) : null;
        slide.hidden = !variant;
        if (!variant) return;

        state.select.value = option.value;
        this.#renderVariant(state, variant);
      });

      this.#refreshSlides();
    }

    #renderVariant(state, variant) {
      state.currentVariant = variant;
      this.#clearError(state);

      if (!variant) {
        if (state.variantInput) state.variantInput.value = '';
        if (state.addButton) state.addButton.disabled = true;
        if (state.addText) state.addText.textContent = this.dataset.labelUnavailable || 'UNAVAILABLE';
        return;
      }

      if (state.variantInput) state.variantInput.value = variant.id;
      if (state.priceCurrent) state.priceCurrent.textContent = variant.price || '';
      if (state.priceCompare) {
        state.priceCompare.textContent = variant.compareAtPrice || '';
        state.priceCompare.hidden = !variant.compareAtPrice;
      }
      if (state.image && variant.image) {
        state.image.src = variant.image;
        state.image.removeAttribute('srcset');
        state.image.removeAttribute('sizes');
      }
      if (state.hasModel && state.select) {
        const value = variant.options?.[state.modelPosition] || '';
        state.select.value = value;
        if (state.modelLabel) state.modelLabel.textContent = value;
      }
      if (state.addButton) state.addButton.disabled = this.#adding || !variant.available;
      if (state.addText) {
        state.addText.textContent = variant.available
          ? this.dataset.labelAdd || '+ ADD'
          : this.dataset.labelSoldOut || 'SOLD OUT';
      }
    }

    #handleModelChange(event) {
      const select = event.target.closest('[data-pia-model-select]');
      if (!select || !this.contains(select)) return;

      const card = select.closest('[data-pia-card]');
      const state = card ? this.#cardStates.get(card) : null;
      if (!state) return;

      const option = select.selectedOptions[0];
      const model = {
        handle: String(option?.dataset.modelHandle || '').trim(),
        name: String(option?.value || '').trim(),
      };
      const variant = this.#findVariantForModel(state, model.name);
      this.#renderVariant(state, variant);
      this.#selectGlobalModel(model);
    }

    #selectGlobalModel(model) {
      if (!model.handle || !model.name) return;

      const globalSelector = document.querySelector('global-model-selector:not([data-auto-select-default])');
      if (
        globalSelector &&
        typeof globalSelector.getAvailableModel === 'function' &&
        typeof globalSelector.selectGlobalModel === 'function'
      ) {
        const availableModel = globalSelector.getAvailableModel(model);
        if (availableModel) {
          globalSelector.selectGlobalModel(availableModel);
          return;
        }
      }

      try {
        root.localStorage.setItem(STORAGE_KEY, JSON.stringify(model));
      } catch (error) {
        void error;
      }
      try {
        root.sessionStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(model));
      } catch (error) {
        void error;
      }

      document.querySelectorAll('[data-seacase-phone-model-label]').forEach((label) => {
        label.textContent = model.name;
      });
      const detail = { ...model, source: 'user' };
      root.dispatchEvent(new CustomEvent(MODEL_EVENT, { detail }));
      document.dispatchEvent(new CustomEvent(LEGACY_MODEL_EVENT, { detail }));
    }

    #refreshSlides() {
      this.visibleSlides = this.slides.filter((slide) => !slide.hidden);
      this.hidden = false;
      if (this.visibleSlides.length === 0) {
        this.dots.hidden = true;
        return;
      }

      this.activeIndex = Math.min(this.activeIndex, this.visibleSlides.length - 1);
      this.#renderDots();
      root.requestAnimationFrame(() => this.#goTo(this.activeIndex, 'auto'));
    }

    #renderDots() {
      this.dots.replaceChildren();
      this.dots.hidden = this.visibleSlides.length <= 1;

      this.visibleSlides.forEach((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `pi2-accessories__dot${index === this.activeIndex ? ' is-active' : ''}`;
        dot.setAttribute('aria-label', slide.dataset.accessoryTitle || String(index + 1));
        dot.setAttribute('aria-current', index === this.activeIndex ? 'true' : 'false');
        dot.addEventListener('click', () => this.#goTo(index, 'smooth'), {
          signal: this.#controller.signal,
        });
        this.dots.appendChild(dot);
      });
    }

    #goTo(index, behavior = 'smooth') {
      if (!this.visibleSlides.length) return;
      const nextIndex = Math.max(0, Math.min(index, this.visibleSlides.length - 1));
      const slide = this.visibleSlides[nextIndex];
      const reducedMotion = root.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      this.viewport.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion ? 'auto' : behavior });
      this.#setActiveDot(nextIndex);
    }

    #setActiveDot(index) {
      this.activeIndex = Math.max(0, Math.min(index, this.visibleSlides.length - 1));
      Array.from(this.dots.children).forEach((dot, dotIndex) => {
        const active = dotIndex === this.activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    #handleScroll() {
      if (!this.visibleSlides.length) return;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      this.visibleSlides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - this.viewport.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      this.#setActiveDot(closestIndex);

      root.clearTimeout(this.#scrollTimer);
      this.#scrollTimer = root.setTimeout(() => {
        if (!this.#dragState) this.#goTo(this.activeIndex, 'smooth');
      }, 100);
    }

    #startDrag(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('button, select, option, input, a')) return;

      this.#dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startScrollLeft: this.viewport.scrollLeft,
        moved: false,
      };
    }

    #moveDrag(event) {
      if (!this.#dragState || event.pointerId !== this.#dragState.pointerId) return;
      const deltaX = event.clientX - this.#dragState.startX;
      const deltaY = event.clientY - this.#dragState.startY;
      if (!this.#dragState.moved) {
        if (Math.abs(deltaY) > Math.abs(deltaX)) return;
        if (Math.abs(deltaX) < 5) return;
      }

      if (!this.#dragState.moved) {
        this.#dragState.moved = true;
        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture?.(event.pointerId);
      }

      this.viewport.scrollLeft = this.#dragState.startScrollLeft - deltaX;
      event.preventDefault();
    }

    #endDrag(event) {
      if (!this.#dragState || event.pointerId !== this.#dragState.pointerId) return;
      const moved = this.#dragState.moved;
      this.#dragState = null;
      this.viewport.classList.remove('is-dragging');

      if (this.viewport.hasPointerCapture?.(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }
      if (moved) {
        this.#blockNextClick = true;
        root.setTimeout(() => {
          this.#blockNextClick = false;
        }, 0);
        this.#goTo(this.activeIndex, 'smooth');
      }
    }

    async #handleAddToCart(event) {
      const form = event.target.closest('[data-pia-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault();

      const card = form.closest('[data-pia-card]');
      const state = card ? this.#cardStates.get(card) : null;
      if (
        this.#adding ||
        !state?.currentVariant?.available ||
        !state.addButton ||
        !state.variantInput
      ) {
        return;
      }

      const variantId = String(state.variantInput.value || '').trim();
      if (!variantId) {
        this.#showError(state, this.dataset.labelUnavailable || 'UNAVAILABLE');
        return;
      }

      root.clearTimeout(state.resetTimer);
      this.#adding = true;
      this.#clearError(state);
      form.setAttribute('aria-busy', 'true');
      this.#getCardStates().forEach((cardState) => {
        if (cardState.addButton) cardState.addButton.disabled = true;
      });
      if (state.addText) state.addText.textContent = this.dataset.labelAdding || 'ADDING...';
      document.dispatchEvent(new CustomEvent('cart:open-checkout-drawer'));

      try {
        const formData = new FormData(form);
        formData.set('id', variantId);
        formData.set('quantity', '1');

        const cartSectionIds = Array.from(document.querySelectorAll('cart-items-component'))
          .map((item) => item.dataset.sectionId)
          .filter(Boolean);
        if (cartSectionIds.length) formData.set('sections', cartSectionIds.join(','));

        const response = await fetch(
          root.Theme?.routes?.cart_add_url || form.action || '/cart/add.js',
          {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          }
        );
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || this.dataset.labelError);
        }

        if (state.addText) state.addText.textContent = this.dataset.labelAdded || 'ADDED';
        this.#adding = false;
        form.removeAttribute('aria-busy');
        this.#getCardStates().forEach((cardState) => {
          if (cardState !== state) this.#renderVariant(cardState, cardState.currentVariant);
        });
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: data,
              sourceId: 'pi-accessories',
              data: {
                source: 'product-form-component',
                itemCount: 1,
                productId: form.dataset.piaProductId,
                variantId,
                sections: data.sections,
              },
            },
          })
        );

        state.resetTimer = root.setTimeout(
          () => this.#renderVariant(state, state.currentVariant),
          1200
        );
      } catch (error) {
        this.#adding = false;
        form.removeAttribute('aria-busy');
        this.#getCardStates().forEach((cardState) => {
          this.#renderVariant(cardState, cardState.currentVariant);
        });
        this.#showError(
          state,
          error?.message || this.dataset.labelError || 'Unable to add this item to cart.'
        );
      }
    }

    #getCardStates() {
      return this.slides
        .map((slide) => {
          const card = slide.querySelector('[data-pia-card]');
          return card ? this.#cardStates.get(card) : null;
        })
        .filter(Boolean);
    }

    #showError(state, message) {
      if (!state.error) return;
      state.error.textContent = message;
      state.error.hidden = false;
    }

    #clearError(state) {
      if (!state.error) return;
      state.error.textContent = '';
      state.error.hidden = true;
    }
  }

  if (!customElements.get('pi-accessories-carousel')) {
    customElements.define('pi-accessories-carousel', PiAccessoriesCarousel);
  }
})(window);
